import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Award, Shield, TrendingUp, Download, Share2, CheckCircle, Settings } from "lucide-react";

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="h-8 w-8 text-slate-900" />
            <span className="text-xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Certificate Machine</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/pricing")} data-testid="nav-pricing-btn">Pricing</Button>
            <Button variant="ghost" onClick={() => navigate("/issuer/login")} data-testid="nav-login-btn">Login</Button>
            <Button onClick={() => navigate("/issuer/signup")} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-6 py-2" data-testid="nav-signup-btn">Get Started</Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-12 md:py-20" data-testid="hero-section">
        <div className="guilloche-bg absolute inset-0 opacity-30"></div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-medium tracking-tight text-slate-900 mb-6" style={{fontFamily: 'Playfair Display'}}>
              Professional Credentials
              <br />
              <span className="text-amber-600">Worth Sharing</span>
            </h1>
            <p className="text-base leading-relaxed text-slate-600 mb-8 max-w-2xl mx-auto">
              Create certificates in minutes - simple, beautiful, and reliable.<br />Your branding, our machine.
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate("/issuer/signup")} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-8 py-3 font-medium" size="lg" data-testid="hero-get-started-btn">Get Started Free</Button>
              <Button onClick={() => navigate("/pricing")} variant="outline" className="bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-lg px-8 py-3 font-medium" size="lg" data-testid="hero-pricing-btn">View Pricing</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-8 md:py-12 bg-slate-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-4" style={{fontFamily: 'Playfair Display'}}>Everything You Need</h2>
            <p className="text-base text-slate-600">Simple. Powerful. Professional.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 bg-white border border-slate-100 shadow-sm rounded-xl hover:border-slate-200 smooth-transition" data-testid="feature-templates">
              <div className="h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center mb-4">
                <Award className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-3" style={{fontFamily: 'Playfair Display'}}>Beautiful Templates</h3>
              <p className="text-base leading-relaxed text-slate-600">20+ professional certificate templates designed to impress. Just add your logo and content.</p>
            </div>

            <div className="p-6 bg-white border border-slate-100 shadow-sm rounded-xl hover:border-slate-200 smooth-transition" data-testid="feature-bulk">
              <div className="h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-3" style={{fontFamily: 'Playfair Display'}}>Bulk Issuance</h3>
              <p className="text-base leading-relaxed text-slate-600">Upload a CSV and issue 100 certificates in under 2 minutes. Automatic email delivery with unique verification links.</p>
            </div>

            <div className="p-6 bg-white border border-slate-100 shadow-sm rounded-xl hover:border-slate-200 smooth-transition" data-testid="feature-verification">
              <div className="h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-3" style={{fontFamily: 'Playfair Display'}}>Public Verification</h3>
              <p className="text-base leading-relaxed text-slate-600">Every certificate gets a unique, shareable verification URL. Recipients can prove authenticity anywhere.</p>
            </div>

            <div className="p-6 bg-white border border-slate-100 shadow-sm rounded-xl hover:border-slate-200 smooth-transition" data-testid="feature-linkedin">
              <div className="h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center mb-4">
                <Share2 className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-3" style={{fontFamily: 'Playfair Display'}}>LinkedIn Ready</h3>
              <p className="text-base leading-relaxed text-slate-600">One-click sharing to LinkedIn with perfectly sized images. Turn recipients into brand ambassadors.</p>
            </div>

            <div className="p-6 bg-white border border-slate-100 shadow-sm rounded-xl hover:border-slate-200 smooth-transition" data-testid="feature-downloads">
              <div className="h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center mb-4">
                <Download className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-3" style={{fontFamily: 'Playfair Display'}}>PDF & PNG Export</h3>
              <p className="text-base leading-relaxed text-slate-600">High-quality PDF for printing, optimized PNG for social media. Recipients get both formats instantly.</p>
            </div>

            <div className="p-6 bg-white border border-slate-100 shadow-sm rounded-xl hover:border-slate-200 smooth-transition" data-testid="feature-tracking">
              <div className="h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-3" style={{fontFamily: 'Playfair Display'}}>Track Engagement</h3>
              <p className="text-base leading-relaxed text-slate-600">See how many times each certificate was viewed, downloaded, and shared. Measure your impact.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-6" style={{fontFamily: 'Playfair Display'}}>Ready to Get Started?</h2>
          <p className="text-base leading-relaxed text-slate-600 mb-8">Join hundreds of creators issuing professional credentials</p>
          <Button onClick={() => navigate("/issuer/signup")} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-8 py-3 font-medium" size="lg" data-testid="cta-signup-btn">Create Your First Certificate</Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">© 2025 Instructing.com, LLC. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href="/privacy" className="text-sm text-slate-500 hover:text-slate-700">Privacy Policy</a>
              <a href="/terms" className="text-sm text-slate-500 hover:text-slate-700">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
